import React from "react";
import { Button, Modal, Typography, Box, Grid, Paper } from "@mui/material";
import { generateClient } from "aws-amplify/api";
import { useState } from "react";
import ViewCSVData from "./ViewCSVData";
import { LoadingButton } from "@mui/lab";
import CSVUploader from "./CSVUploader";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from "uuid";
import FilePreview from "./FilePreview";
import UploadFile from "./UploadFile";
import { createContent } from "../../graphql/mutations";
import { uploadData } from "aws-amplify/storage";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import { useParams } from "react-router-dom";
import Rights from "../Content/Rights";
import LicensingTerm from "./LicensingTerm";
import LicensingRegion from "./LicensingRegion";
import { convertToIso6391 } from "../../helper/convertToIso";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "auto",
  minWidth: "50%",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  maxHeight: "800px",
  overflow: "scroll",
  p: 4,
};

// Styled component for file input
const VisuallyHiddenInput = styled("input")`
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  height: 1px;
  overflow: hidden;
  position: absolute;
  bottom: 0;
  left: 0;
  white-space: nowrap;
  width: 1px;
`;

const UploadAlbumMetaData = ({ formik: parentFormik }) => {
  const client = generateClient();
  const contentForm = useSelector((state) => state.contentForm);
  const params = useParams();

  const [csvData, setCSVData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [fileContents, setFileContents] = useState({
    masterFileContent: {
      file: "",
      filePath: "",
    },
    posterFileContent: {
      file: "",
      filePath: "",
    },
  });
  const [open, setOpen] = useState(false);
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  
  const uploadMasterFile = async () => {
    try {
      const filePath = fileContents.masterFileContent.filePath;

      await uploadData({
        key: filePath,
        data: fileContents.masterFileContent.file,
      }).result;
    } catch (error) {
      console.error("An error occurred during the upload process:", error);
    }
  };
  
  const uploadContentData = async () => {
    try {
      if (fileContents.posterFileContent.filePath) {
        const result = await uploadData({
          key: fileContents.posterFileContent.filePath,
          data: fileContents.posterFileContent.file,
        }).result;
      }
    } catch (error) {
      console.error("An error occurred during the upload process:", error);
    }
  };

  const agreementForm = useSelector((state) => state.agreementForm);
  const numberOfRefreshCycles = agreementForm?.numberOfRefreshCycles;
  let cyclesOptions = [];

  for (let i = 0; i < numberOfRefreshCycles; i++) {
    cyclesOptions.push({ value: i + 1, label: i + 1 });
  }

  const formik = useFormik({
    initialValues: {
      title: "",
      albumId: "",
      type: contentForm.contentType,
      category: contentForm.category,
      currentCycle: params?.currentCycle || 1,
      imdbRating: "",
      rottenRating: "",
      rating: "",
      genre: [""],
      language: "",
      duration: 0,
      author: "",
      publisher: "",
      filePath: "",
      trailerFilePath: "",
      synopsis: "",
      year: "",
      composer: "",
      artist: "",
      director: "",
      actors: [""],
      posters: [""],
    },

    onSubmit: async (values) => {
      try {
        setLoading(true);
        await uploadMasterFile();
        await uploadContentData();
        const content = {
          agreementId: agreementForm?.id,
          title: csvData[0]?.["Album Title"],
          synopsis: csvData[0]?.["Description"],
          type: contentForm.contentType,
          albumId: contentForm.albumId,
          category: contentForm.category,
          artist: csvData[0]?.Artist,
          status: "Pending",
          language: "eng",
          duration: csvData[0]?.["Running Time"],
          filePath: fileContents.masterFileContent.filePath,
          trailerFilePath: "",
          year: csvData[0]?.Year,
          posters: [fileContents.masterFileContent.filePath],
          genre: csvData[0]?.Genre?.split(",").map((item) => item.trim()),
          isSeries: false,
          isDrmProtected: false,
          isAlbum: true,
        };
        console.log(content, "content uploader");
        const response = await client.graphql({
          query: createContent,
          variables: {
            input: content,
          },
        });
        if (response.data.createContent) {
          toast.success("Album Metadata has been submitted!");
          handleClose();
        }
      } catch (error) {
        toast.error("Internal Server Error!");
        console.log(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const handleFilePath = (file) => {
    const fileExtension = file.name.split(".").pop().toLowerCase();

    let fileType = "";
    const guid = uuidv4();

    let filePath;
    const type = contentForm.contentType;
    const category = contentForm.category;
    let title = "";
    if (csvData && csvData.length > 0) {
      title = csvData[0].Title;
    }

    filePath = `${type?.replace(/\s+/g, "")}/${category?.replace(
      /\s+/g,
      ""
    )}/${title?.replace(/\s+/g, "")}/${guid}.${fileExtension}`;

    return { filePath, fileType };
  };

  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];

    if (uploadedFile) {
      const { filePath, fileType } = handleFilePath(uploadedFile);
      if (event.target.name === "default") {
        setFileContents({
          ...fileContents,
          posterFileContent: {
            file: uploadedFile,
            filePath,
          },
        });
      } else {
        setFileContents({
          ...fileContents,
          masterFileContent: {
            file: uploadedFile,
            filePath: filePath,
          },
        });
      }
    }
  };

  return (
    <div>
      <Button
        variant="outlined"
        fullWidth
        sx={{ marginY: "10px" }}
        onClick={handleOpen}
      >
        Upload Album MetaData
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <form onSubmit={formik.handleSubmit}>
            <Box display={"flex"} gap={4} alignItems={"center"}>
              <Box flexBasis={"50%"}>
                <CSVUploader onFileLoad={(data) => setCSVData(data)} />
                {csvData && csvData.length > 0 && (
                  <>
                    <Typography sx={{ color: "green" }}>
                      {" "}
                      CSV Uploaded
                    </Typography>
                    <ViewCSVData csvData={csvData} />
                  </>
                )}
              </Box>
              <Box flexBasis={"50%"}>
                <Paper
                  elevation={0}
                  sx={{ p: 2, border: "1px dashed grey", mb: 2 }}
                >
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    Upload Album Poster
                    <VisuallyHiddenInput
                      type="file"
                      name="master"
                      accept="image/*"
                      onChange={handleFileChange}
                      required
                    />
                  </Button>
                  <Typography 
                    variant="caption" 
                    display="block" 
                    sx={{ mt: 1 }}
                  >
                    {fileContents.masterFileContent.file ? (
                      <img 
                        src={URL.createObjectURL(fileContents.masterFileContent.file)} 
                        alt="Album poster preview"
                        style={{ maxWidth: "100%", maxHeight: "200px" }}
                      />
                    ) : (
                      "No file selected"
                    )}
                  </Typography>
                </Paper>
                
                <Paper
                  elevation={0}
                  sx={{ p: 2, border: "1px dashed grey" }}
                >
                  <Button
                    component="label"
                    variant="contained"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                  >
                    Upload Horizontal Poster
                    <VisuallyHiddenInput
                      type="file"
                      name="default"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </Button>
                  <Typography 
                    variant="caption" 
                    display="block" 
                    sx={{ mt: 1 }}
                  >
                    {fileContents.posterFileContent.file ? (
                      <img 
                        src={URL.createObjectURL(fileContents.posterFileContent.file)} 
                        alt="Horizontal poster preview"
                        style={{ maxWidth: "100%", maxHeight: "200px" }}
                      />
                    ) : (
                      "No file selected"
                    )}
                  </Typography>
                </Paper>
              </Box>
            </Box>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isLoading}
              fullWidth
              sx={{ marginTop: 2 }}
            >
              Submit
            </LoadingButton>
          </form>
        </Box>
      </Modal>
    </div>
  );
};

export default UploadAlbumMetaData;